/**
 * Columns you can drag, and the widths they are left at.
 *
 * The room is the one thing on screen whose right width depends on what is in
 * it: a wall of `stagepost say` output wants a wide room, and long room names
 * want a wide repository list. The list was a fixed number lifted from the
 * prototype, which is the right default and the wrong rule.
 *
 * A width is remembered per browser rather than per account: it is a property
 * of the screen it is being read on, and the same person on a laptop and on a
 * 34-inch display does not want one answer carried between them.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * How much of the window one column may take, however far it is dragged.
 *
 * Dragging a column to its maximum on a small window leaves the room narrower
 * than a line of code. The cap is applied to what is *shown*, not to what was asked for,
 * so widening the window gives back the width it took away.
 */
const SHARE_OF_WINDOW = 0.3

/** What one arrow key moves a column, in pixels. */
const KEYBOARD_STEP = 16

export interface ResizableColumn {
  width: number
  min: number
  max: number
  /** Live, during a drag. */
  resizeTo: (width: number) => void
  /** Keep the width as it now stands. Called when a drag ends, not during it. */
  remember: () => void
  /** Back to the width the app ships with. */
  reset: () => void
}

function clamp(width: number, min: number, max: number): number {
  const share = typeof window === 'undefined' ? Infinity : window.innerWidth * SHARE_OF_WINDOW
  return Math.round(Math.max(min, Math.min(width, max, Math.max(min, share))))
}

function stored(key: string): number | null {
  try {
    const raw = localStorage.getItem(key)
    const width = raw === null ? Number.NaN : Number(raw)
    return Number.isFinite(width) ? width : null
  } catch {
    // A browser with site data turned off. A column that cannot be remembered
    // is still a column that can be dragged.
    return null
  }
}

/**
 * One column's width: what it is, and what it was left at last time.
 *
 * `min` and `max` are the bounds of the design rather than of the layout —
 * the layout's own limit is the share of the window above, which changes as
 * the window does and so cannot be a constant.
 */
export function useColumnWidth(
  key: string, fallback: number, min: number, max: number,
): ResizableColumn {
  // What was asked for, which is not always what is shown: a narrow window
  // caps it, and widening the window afterwards is expected to give it back
  // rather than to find it was thrown away.
  const preferred = useRef(stored(key) ?? fallback)
  const [width, setWidth] = useState(() => clamp(preferred.current, min, max))

  const resizeTo = useCallback((next: number) => {
    preferred.current = next
    setWidth(clamp(next, min, max))
  }, [min, max])

  const remember = useCallback(() => {
    try {
      localStorage.setItem(key, String(clamp(preferred.current, min, max)))
    } catch {
      // Nothing to do, and nothing worth telling anyone: the column is the
      // width they dragged it to for as long as this tab is open.
    }
  }, [key, min, max])

  const reset = useCallback(() => {
    resizeTo(fallback)
    remember()
  }, [resizeTo, remember, fallback])

  useEffect(() => {
    function onResize() {
      setWidth((current) => {
        const next = clamp(preferred.current, min, max)
        return next === current ? current : next
      })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [min, max])

  return { width, min, max, resizeTo, remember, reset }
}

interface Drag {
  from: number
  width: number
}

/**
 * The grab strip on a column's edge.
 *
 * Absolutely positioned over the gap beside its column rather than sitting in
 * the flex row, because a real element between two columns is a third column —
 * it takes the row's gap on both sides and the layout grows a seam wherever a
 * handle appears.
 */
export function ColumnResizer({ column, label, edge = 'right' }: {
  column: ResizableColumn
  label: string
  /** The side of its column the strip sits on. A column on the right grows leftward. */
  edge?: 'left' | 'right'
}) {
  const { width, min, max, resizeTo, remember, reset } = column
  const outward = edge === 'right' ? 1 : -1
  const [drag, setDrag] = useState<Drag | null>(null)
  const [hint, setHint] = useState(false)

  // On the window rather than on the strip: a pointer moving faster than React
  // re-renders leaves a 13px target behind, and a drag that stops when the
  // cursor overshoots is a drag that feels broken.
  useEffect(() => {
    if (!drag) return
    function onMove(event: PointerEvent) {
      resizeTo(drag!.width + outward * (event.clientX - drag!.from))
    }
    function onUp() {
      setDrag(null)
      remember()
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    // Without this a drag across the room selects every message it passes.
    const previous = document.body.style.userSelect
    document.body.style.userSelect = 'none'
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      document.body.style.userSelect = previous
    }
  }, [drag, outward, resizeTo, remember])

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault()
      resizeTo(width + (event.key === 'ArrowRight' ? 1 : -1) * outward * KEYBOARD_STEP)
      remember()
    } else if (event.key === 'Home') {
      event.preventDefault()
      reset()
    }
  }

  const lit = drag !== null || hint
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      title={`${label} — double-click to reset`}
      onPointerDown={(event) => {
        if (event.button !== 0) return
        event.preventDefault()
        setDrag({ from: event.clientX, width })
      }}
      onDoubleClick={reset}
      onKeyDown={onKeyDown}
      onPointerEnter={() => setHint(true)}
      onPointerLeave={() => setHint(false)}
      onFocus={() => setHint(true)}
      onBlur={() => setHint(false)}
      style={{
        position: 'absolute', top: 0, bottom: 0, width: 13, zIndex: 5,
        [edge]: -7,
        display: 'flex', justifyContent: 'center', alignItems: 'stretch',
        cursor: 'col-resize', touchAction: 'none',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 2, borderRadius: 2, transition: 'background .12s ease',
          background: lit ? 'var(--border-strong)' : 'transparent',
        }}
      />
    </div>
  )
}
